// packages/client/src/api/auth.ts
import "zod";

// packages/client/src/http/dispatcher.ts
import z from "zod";

// packages/client/src/http/interceptors.ts
var createHeadersInterceptor = (headers, requestHeaders) => {
  return {
    before({ init, url }) {
      for (const [key, value] of new Headers(requestHeaders)) {
        if (value !== void 0 && !init.headers.has(key)) {
          init.headers.set(key, value);
        }
      }
      for (const [key, value] of Object.entries(headers)) {
        if (value !== void 0 && !init.headers.has(key)) {
          init.headers.set(key, value);
        }
      }
      return { init, url };
    }
  };
};
var createBaseUrlInterceptor = (baseUrl) => {
  return {
    before({ init, url }) {
      if (url.protocol === "local:") {
        return {
          init,
          url: new URL(url.href.replace("local://", baseUrl))
        };
      }
      return { init, url };
    }
  };
};

// packages/client/src/http/request.ts
function template(templateString, templateVariables) {
  const nargs = /{([0-9a-zA-Z_]+)}/g;
  return templateString.replace(nargs, (match, key, index) => {
    if (templateString[index - 1] === "{" && templateString[index + match.length] === "}") {
      return key;
    }
    const result = key in templateVariables ? templateVariables[key] : null;
    return result === null || result === void 0 ? "" : String(result);
  });
}
var Serializer = class {
  input;
  props;
  constructor(input, props) {
    this.input = input;
    this.props = props;
  }
  serialize(path) {
    const params = this.props.inputParams.reduce(
      (acc, key) => {
        acc[key] = this.input[key];
        return acc;
      },
      {}
    );
    const url = new URL(template(path, params), `local://`);
    const headers = new Headers({});
    for (const header of this.props.inputHeaders) {
      headers.set(header, this.input[header]);
    }
    for (const key of this.props.inputQuery) {
      const value = this.input[key];
      if (value !== void 0) {
        if (Array.isArray(value)) {
          for (const item of value) {
            url.searchParams.append(key, String(item));
          }
        } else {
          url.searchParams.set(key, String(value));
        }
      }
    }
    return {
      body: this.getBody(),
      url,
      headers: this.getHeaders()
    };
  }
};
var JsonSerializer = class extends Serializer {
  getBody() {
    const body = {};
    if (this.props.inputBody.length === 1 && this.props.inputBody[0] === "$body") {
      return JSON.stringify(this.input.$body);
    }
    for (const prop of this.props.inputBody) {
      body[prop] = this.input[prop];
    }
    return JSON.stringify(body);
  }
  getHeaders() {
    return {
      "Content-Type": "application/json",
      Accept: "application/json"
    };
  }
};
var EmptySerializer = class extends Serializer {
  getBody() {
    return null;
  }
  getHeaders() {
    return {};
  }
};
function json(input, props) {
  return new JsonSerializer(input, props);
}
function empty(input, props) {
  return new EmptySerializer(input, props);
}
function toRequest(endpoint, serializer) {
  const [method, path] = endpoint.split(" ");
  const input = serializer.serialize(path);
  return {
    url: input.url,
    init: {
      method,
      headers: new Headers(input.headers),
      body: method === "GET" ? void 0 : input.body
    }
  };
}

// packages/client/src/http/parse-response.ts
import { parse } from "fast-content-type-parse";
function isBinaryContentType(contentType) {
  const type = contentType.toLowerCase();
  if (type.startsWith("image/")) {
    return true;
  }
  if (type.startsWith("audio/")) {
    return true;
  }
  if (type.startsWith("video/")) {
    return true;
  }
  switch (type) {
    case "application/pdf":
    case "application/zip":
    case "application/gzip":
    case "application/x-7z-compressed":
    case "application/x-tar":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    case "application/vnd.ms-excel":
    case "application/vnd.ms-powerpoint":
    case "application/msword":
    case "application/octet-stream":
      return true;
    default:
      return false;
  }
}
async function buffered(response) {
  const contentType = response.headers.get("Content-Type");
  if (!contentType) {
    throw new Error("Content-Type header is missing");
  }
  if (response.status === 204) {
    return null;
  }
  const { type } = parse(contentType);
  if (isBinaryContentType(type)) {
    return response.blob();
  }
  if (type.startsWith("text/")) {
    return response.text();
  }
  switch (type) {
    case "application/json":
      return response.json();
    case "application/xml":
      return response.text();
    case "application/x-www-form-urlencoded": {
      const text = await response.text();
      return Object.fromEntries(new URLSearchParams(text));
    }
    case "multipart/form-data":
      return response.formData();
    default:
      throw new Error(`Unsupported content type: ${contentType}`);
  }
}

// packages/client/src/http/response.ts
var APIResponse = class {
  static status;
  status;
  data;
  headers;
  constructor(status, headers, data) {
    this.status = status;
    this.headers = headers;
    this.data = data;
  }
  static create(status, headers, data) {
    return new this(status, headers, data);
  }
};
var APIError = class extends APIResponse {
  static create(status, headers, data) {
    return new this(status, headers, data);
  }
};
var Ok = class _Ok extends APIResponse {
  static status = 200;
  constructor(headers, data) {
    super(_Ok.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Created = class _Created extends APIResponse {
  static status = 201;
  constructor(headers, data) {
    super(_Created.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Accepted = class _Accepted extends APIResponse {
  static status = 202;
  constructor(headers, data) {
    super(_Accepted.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var NoContent = class _NoContent extends APIResponse {
  static status = 204;
  constructor(headers) {
    super(_NoContent.status, headers, null);
  }
  static create(status, headers) {
    return new this(headers);
  }
};
var BadRequest = class _BadRequest extends APIError {
  static status = 400;
  constructor(headers, data) {
    super(_BadRequest.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Unauthorized = class _Unauthorized extends APIError {
  static status = 401;
  constructor(headers, data) {
    super(_Unauthorized.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var PaymentRequired = class _PaymentRequired extends APIError {
  static status = 402;
  constructor(headers, data) {
    super(_PaymentRequired.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Forbidden = class _Forbidden extends APIError {
  static status = 403;
  constructor(headers, data) {
    super(_Forbidden.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var NotFound = class _NotFound extends APIError {
  static status = 404;
  constructor(headers, data) {
    super(_NotFound.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var MethodNotAllowed = class _MethodNotAllowed extends APIError {
  static status = 405;
  constructor(headers, data) {
    super(_MethodNotAllowed.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var NotAcceptable = class _NotAcceptable extends APIError {
  static status = 406;
  constructor(headers, data) {
    super(_NotAcceptable.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Conflict = class _Conflict extends APIError {
  static status = 409;
  constructor(headers, data) {
    super(_Conflict.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Gone = class _Gone extends APIError {
  static status = 410;
  constructor(headers, data) {
    super(_Gone.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var PreconditionFailed = class _PreconditionFailed extends APIError {
  static status = 412;
  constructor(headers, data) {
    super(_PreconditionFailed.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var UnprocessableEntity = class _UnprocessableEntity extends APIError {
  static status = 422;
  constructor(headers, data) {
    super(_UnprocessableEntity.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var TooManyRequests = class _TooManyRequests extends APIError {
  static status = 429;
  constructor(headers, data) {
    super(_TooManyRequests.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var PayloadTooLarge = class _PayloadTooLarge extends APIError {
  static status = 413;
  constructor(headers, data) {
    super(_PayloadTooLarge.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var UnsupportedMediaType = class _UnsupportedMediaType extends APIError {
  static status = 415;
  constructor(headers, data) {
    super(_UnsupportedMediaType.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var InternalServerError = class _InternalServerError extends APIError {
  static status = 500;
  constructor(headers, data) {
    super(_InternalServerError.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var NotImplemented = class _NotImplemented extends APIError {
  static status = 501;
  constructor(headers, data) {
    super(_NotImplemented.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var BadGateway = class _BadGateway extends APIError {
  static status = 502;
  constructor(headers, data) {
    super(_BadGateway.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var ServiceUnavailable = class _ServiceUnavailable extends APIError {
  static status = 503;
  constructor(headers, data) {
    super(_ServiceUnavailable.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var GatewayTimeout = class _GatewayTimeout extends APIError {
  static status = 504;
  constructor(headers, data) {
    super(_GatewayTimeout.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};

// packages/client/src/http/dispatcher.ts
var fetchType = z.function().args(z.instanceof(Request)).returns(z.promise(z.instanceof(Response))).optional();
async function parse2(outputs27, response, mapper) {
  let output = null;
  let parser = buffered;
  for (const outputType of outputs27) {
    if ("parser" in outputType) {
      parser = outputType.parser;
      if (isTypeOf(outputType.type, APIResponse)) {
        if (response.status === outputType.type.status) {
          output = outputType.type;
          break;
        }
      }
    } else if (isTypeOf(outputType, APIResponse)) {
      if (response.status === outputType.status) {
        output = outputType;
        break;
      }
    }
  }
  if (response.ok) {
    const data = await parser(response);
    const mapped = mapper ? mapper(data) : data;
    const apiresponse = (output || APIResponse).create(
      response.status,
      response.headers,
      mapped
    );
    return apiresponse;
  }
  throw (output || APIError).create(
    response.status,
    response.headers,
    await parser(response)
  );
}
function isTypeOf(instance, baseType) {
  if (instance === baseType) {
    return true;
  }
  const prototype = Object.getPrototypeOf(instance);
  if (prototype === null) {
    return false;
  }
  return isTypeOf(prototype, baseType);
}
var Dispatcher = class {
  #interceptors = [];
  #fetch;
  constructor(interceptors, fetch2) {
    this.#interceptors = interceptors;
    this.#fetch = fetch2;
  }
  async send(config, outputs27, signal, mapper) {
    for (const interceptor of this.#interceptors) {
      if (interceptor.before) {
        config = await interceptor.before(config);
      }
    }
    let response = await (this.#fetch ?? fetch)(
      new Request(config.url, config.init),
      {
        ...config.init,
        signal
      }
    );
    for (let i = this.#interceptors.length - 1; i >= 0; i--) {
      const interceptor = this.#interceptors[i];
      if (interceptor.after) {
        response = await interceptor.after(response.clone());
      }
    }
    if (mapper) {
      return await parse2(outputs27, response, mapper);
    }
    return await parse2(outputs27, response);
  }
};

// packages/client/src/inputs/auth.ts
import { z as z2 } from "zod";
var changePasswordSchema = z2.object({ newPassword: z2.string() });

// packages/client/src/api/auth.ts
var auth_default = {
  "POST /me/password": {
    schema: changePasswordSchema,
    output: [
      NoContent,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /me/password",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["newPassword"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/availability.ts
import "zod";

// packages/client/src/inputs/availability.ts
import { z as z4 } from "zod";
var getMyAvailabilitySchema = z4.object({
  weekday: z4.enum([
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
    "SUNDAY"
  ]).optional()
});
var getDoctorAvailabilitySchema = z4.object({ doctorId: z4.string() });
var setDoctorAvailabilitySchema = z4.object({
  availability: z4.array(
    z4.object({
      weekday: z4.enum([
        "MONDAY",
        "TUESDAY",
        "WEDNESDAY",
        "THURSDAY",
        "FRIDAY",
        "SATURDAY",
        "SUNDAY"
      ]),
      slots: z4.array(
        z4.object({ startTime: z4.number().int(), endTime: z4.number().int() })
      )
    })
  ).optional(),
  doctorId: z4.string()
});
var getAvailableSlotsBySpecialitySchema = z4.object({
  specialityId: z4.string().uuid(),
  date: z4.string().datetime()
});
var getAvailableSlotsForDoctorSchema = z4.object({
  doctorId: z4.string(),
  date: z4.string().datetime()
});
var getReservationAvailableSlotsUntilMonthEndSchema = z4.object({
  specialityId: z4.string().uuid(),
  date: z4.string().datetime()
});
var getMyOverridesSchema = z4.object({}).catchall(z4.unknown());
var setDoctorOverridesSchema = z4.object({
  overrides: z4.array(
    z4.object({
      date: z4.string().datetime(),
      slots: z4.array(
        z4.object({ startTime: z4.number().int(), endTime: z4.number().int() })
      )
    })
  ).optional(),
  doctorId: z4.string()
});

// packages/client/src/api/availability.ts
var availability_default = {
  "GET /availability/me": {
    schema: getMyAvailabilitySchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /availability/me",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["weekday"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /availability/doctor/{doctorId}": {
    schema: getDoctorAvailabilitySchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /availability/doctor/{doctorId}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["doctorId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PUT /availability/{doctorId}": {
    schema: setDoctorAvailabilitySchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PUT /availability/{doctorId}",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["availability"],
          inputParams: ["doctorId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /availability/speciality/{specialityId}/slots": {
    schema: getAvailableSlotsBySpecialitySchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /availability/speciality/{specialityId}/slots",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["date"],
          inputBody: [],
          inputParams: ["specialityId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /availability/doctor/{doctorId}/slots": {
    schema: getAvailableSlotsForDoctorSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /availability/doctor/{doctorId}/slots",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["date"],
          inputBody: [],
          inputParams: ["doctorId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /availability/slots": {
    schema: getReservationAvailableSlotsUntilMonthEndSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /availability/slots",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["specialityId", "date"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /availability/me/overrides": {
    schema: getMyOverridesSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /availability/me/overrides",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PUT /availability/{doctorId}/overrides": {
    schema: setDoctorOverridesSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PUT /availability/{doctorId}/overrides",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["overrides"],
          inputParams: ["doctorId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/btob-patients.ts
import "zod";

// packages/client/src/inputs/btob-patients.ts
import { z as z6 } from "zod";
var createB2BPatientSchema = z6.object({
  email: z6.string().email(),
  name: z6.string(),
  phoneNumber: z6.string()
});
var listB2BPatientsSchema = z6.object({
  query: z6.union([z6.string(), z6.string(), z6.string()]).optional(),
  page: z6.coerce.number().int().gt(0).optional().default(1),
  pageSize: z6.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z6.enum(["name", "email"]).optional().default("name"),
  sortOrder: z6.enum(["asc", "desc"]).optional().default("desc")
});
var getB2BPatientSchema = z6.object({ id: z6.string() });
var updateB2BPatientQuotasSchema = z6.object({
  printingUsageLimit: z6.number().int().min(0).optional(),
  usageLimit: z6.number().int().min(0).optional(),
  id: z6.string()
});
var listB2BReservationsSchema = z6.object({
  status: z6.enum([
    "WAITING_DOCTOR_ASSIGNMENT",
    "WAITING_DOCTOR_CONFIRMATION",
    "CONFIRMED",
    "FINISHED",
    "CANCELED",
    "AVAILABLE_FOR_FOLLOWUP",
    "EXPIRED"
  ]).optional(),
  query: z6.union([z6.string(), z6.string(), z6.string()]).optional(),
  page: z6.coerce.number().int().gt(0).optional().default(1),
  pageSize: z6.coerce.number().int().min(1).max(100).optional().default(50)
});
var listB2BSecondOpinionsSchema = z6.object({
  status: z6.enum([
    "WAITING_DOCTOR_ASSIGNMENT",
    "WAITING_DOCTOR_CONFIRMATION",
    "CONFIRMED",
    "FINISHED"
  ]).optional(),
  query: z6.union([z6.string(), z6.string(), z6.string()]).optional(),
  page: z6.coerce.number().int().gt(0).optional().default(1),
  pageSize: z6.coerce.number().int().min(1).max(100).optional().default(50)
});

// packages/client/src/api/btob-patients.ts
var btob_patients_default = {
  "POST /patients/b2b": {
    schema: createB2BPatientSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType,
      InternalServerError
    ],
    toRequest(input) {
      return toRequest(
        "POST /patients/b2b",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["email", "name", "phoneNumber"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /b2b/patients": {
    schema: listB2BPatientsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /b2b/patients",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["query", "page", "pageSize", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /b2b/patients/{id}": {
    schema: getB2BPatientSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /b2b/patients/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /b2b/patients/{id}": {
    schema: updateB2BPatientQuotasSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /b2b/patients/{id}",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["printingUsageLimit", "usageLimit"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /b2b/reservations": {
    schema: listB2BReservationsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /b2b/reservations",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["status", "query", "page", "pageSize"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /b2b/second-opinions": {
    schema: listB2BSecondOpinionsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /b2b/second-opinions",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["status", "query", "page", "pageSize"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/coordinator.ts
import "zod";

// packages/client/src/inputs/coordinator.ts
import { z as z8 } from "zod";
var getCurrentCoordinatorSchema = z8.object({}).catchall(z8.unknown());
var createCoordinatorSchema = z8.object({
  email: z8.string().email(),
  firstName: z8.string(),
  lastName: z8.string()
});
var listCoordinatorsSchema = z8.object({
  query: z8.union([z8.string(), z8.string(), z8.string()]).optional(),
  page: z8.coerce.number().int().gt(0).optional().default(1),
  pageSize: z8.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z8.string().optional(),
  sortOrder: z8.enum(["asc", "desc"]).optional().default("asc")
});
var getCoordinatorSchema = z8.object({ id: z8.string() });
var updateCoordinatorSchema = z8.object({
  email: z8.string().email().optional(),
  firstName: z8.string().optional(),
  lastName: z8.string().optional(),
  id: z8.string()
});
var deleteCoordinatorSchema = z8.object({ id: z8.string() });

// packages/client/src/api/coordinator.ts
var coordinator_default = {
  "GET /coordinators/me": {
    schema: getCurrentCoordinatorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /coordinators/me",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /coordinators": {
    schema: createCoordinatorSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType,
      InternalServerError
    ],
    toRequest(input) {
      return toRequest(
        "POST /coordinators",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["email", "firstName", "lastName"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /coordinators": {
    schema: listCoordinatorsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /coordinators",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["query", "page", "pageSize", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /coordinators/{id}": {
    schema: getCoordinatorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /coordinators/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /coordinators/{id}": {
    schema: updateCoordinatorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /coordinators/{id}",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["email", "firstName", "lastName"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /coordinators/{id}": {
    schema: deleteCoordinatorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /coordinators/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/diagnosis.ts
import "zod";

// packages/client/src/inputs/diagnosis.ts
import { z as z10 } from "zod";
var createDiagnosisSchema = z10.object({
  icd10CodeIds: z10.array(z10.string().uuid()).optional().default([]),
  icd10Notes: z10.string().optional(),
  labTestIds: z10.array(z10.string().uuid()).optional().default([]),
  labTestNotes: z10.string().optional(),
  prescriptionNotes: z10.string().optional(),
  prescriptions: z10.array(
    z10.object({
      medicationId: z10.string().uuid(),
      dosageAmount: z10.number().int().optional().default(1),
      period: z10.string()
    })
  ).optional().default([]),
  privateNotes: z10.string().optional(),
  sickLeaves: z10.coerce.number().int().min(0).max(365).optional(),
  reservationId: z10.string().uuid()
});
var getDiagnosesByReservationSchema = z10.object({
  reservationId: z10.string().uuid()
});
var getDiagnosisByIdSchema = z10.object({ id: z10.string().uuid() });
var updateDiagnosisSchema = z10.object({
  icd10CodeIds: z10.array(z10.string().uuid()).optional(),
  icd10Notes: z10.string().optional(),
  labTestIds: z10.array(z10.string().uuid()).optional(),
  labTestNotes: z10.string().optional(),
  prescriptionNotes: z10.string().optional(),
  prescriptions: z10.array(
    z10.object({
      medicationId: z10.string().uuid(),
      dosageAmount: z10.number().int().min(1),
      period: z10.string()
    })
  ).optional(),
  privateNotes: z10.string().optional(),
  sickLeaves: z10.coerce.number().int().min(0).max(365).optional(),
  id: z10.string().uuid()
});
var getDiagnosesByPatientSchema = z10.object({
  patientId: z10.string(),
  page: z10.coerce.number().int().gt(0).optional().default(1),
  pageSize: z10.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z10.enum(["createdAt", "updatedAt", "leaves"]).optional().default("createdAt"),
  sortOrder: z10.enum(["asc", "desc"]).optional().default("asc")
});
var getDiagnosesByDoctorSchema = z10.object({
  doctorId: z10.string(),
  page: z10.coerce.number().int().gt(0).optional().default(1),
  pageSize: z10.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z10.enum(["createdAt", "updatedAt", "leaves"]).optional().default("createdAt"),
  sortOrder: z10.enum(["asc", "desc"]).optional().default("asc"),
  startDate: z10.string().datetime().optional(),
  endDate: z10.string().datetime().optional()
});
var getMyDiagnosesSchema = z10.object({
  page: z10.coerce.number().int().gt(0).optional().default(1),
  pageSize: z10.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z10.enum(["createdAt", "updatedAt", "leaves"]).optional().default("createdAt"),
  sortOrder: z10.enum(["asc", "desc"]).optional().default("asc"),
  startDate: z10.string().datetime().optional(),
  endDate: z10.string().datetime().optional()
});
var getMyPatientDiagnosesSchema = z10.object({
  page: z10.coerce.number().int().gt(0).optional().default(1),
  pageSize: z10.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z10.enum(["createdAt", "updatedAt", "leaves"]).optional().default("createdAt"),
  sortOrder: z10.enum(["asc", "desc"]).optional().default("asc")
});

// packages/client/src/api/diagnosis.ts
var diagnosis_default = {
  "POST /reservations/{reservationId}/diagnosis": {
    schema: createDiagnosisSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /reservations/{reservationId}/diagnosis",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "icd10CodeIds",
            "icd10Notes",
            "labTestIds",
            "labTestNotes",
            "prescriptionNotes",
            "prescriptions",
            "privateNotes",
            "sickLeaves"
          ],
          inputParams: ["reservationId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /reservations/{reservationId}/diagnosis": {
    schema: getDiagnosesByReservationSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /reservations/{reservationId}/diagnosis",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["reservationId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /diagnosis/{id}": {
    schema: getDiagnosisByIdSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /diagnosis/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /diagnosis/{id}": {
    schema: updateDiagnosisSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /diagnosis/{id}",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "icd10CodeIds",
            "icd10Notes",
            "labTestIds",
            "labTestNotes",
            "prescriptionNotes",
            "prescriptions",
            "privateNotes",
            "sickLeaves"
          ],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /patients/{patientId}/diagnosis": {
    schema: getDiagnosesByPatientSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /patients/{patientId}/diagnosis",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["page", "pageSize", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: ["patientId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /doctors/{doctorId}/diagnosis": {
    schema: getDiagnosesByDoctorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /doctors/{doctorId}/diagnosis",
        empty(input, {
          inputHeaders: [],
          inputQuery: [
            "page",
            "pageSize",
            "sortBy",
            "sortOrder",
            "startDate",
            "endDate"
          ],
          inputBody: [],
          inputParams: ["doctorId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /doctors/me/diagnosis": {
    schema: getMyDiagnosesSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /doctors/me/diagnosis",
        empty(input, {
          inputHeaders: [],
          inputQuery: [
            "page",
            "pageSize",
            "sortBy",
            "sortOrder",
            "startDate",
            "endDate"
          ],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /patients/me/diagnosis": {
    schema: getMyPatientDiagnosesSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /patients/me/diagnosis",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["page", "pageSize", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/doctor.ts
import "zod";

// packages/client/src/inputs/doctor.ts
import { z as z12 } from "zod";
var createDoctorSchema = z12.object({
  birthDate: z12.string().datetime(),
  email: z12.string().email(),
  firstName: z12.string(),
  gender: z12.enum(["male", "female"]),
  lastName: z12.string(),
  phoneNumber: z12.string().optional(),
  scfhsNo: z12.string(),
  specialityId: z12.string(),
  timezone: z12.string().optional()
});
var listDoctorsSchema = z12.object({
  specialityId: z12.string().nullable().optional(),
  email: z12.string().nullable().optional(),
  query: z12.union([z12.string(), z12.string(), z12.string()]).optional(),
  page: z12.coerce.number().int().gt(0).optional().default(1),
  pageSize: z12.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z12.string().nullable().optional(),
  sortOrder: z12.enum(["asc", "desc"]).optional().default("asc")
});
var getCurrentDoctorSchema = z12.object({}).catchall(z12.unknown());
var getDoctorEarningsSchema = z12.object({}).catchall(z12.unknown());
var getDoctorSchema = z12.object({ id: z12.string() });
var updateDoctorSchema = z12.object({
  birthDate: z12.string().datetime().optional(),
  email: z12.string().email().optional(),
  firstName: z12.string().optional(),
  gender: z12.enum(["male", "female"]).optional(),
  lastName: z12.string().optional(),
  phoneNumber: z12.string().optional(),
  scfhsNo: z12.string().optional(),
  specialityId: z12.string().nullable().optional(),
  timezone: z12.string().optional(),
  id: z12.string()
});
var deleteDoctorSchema = z12.object({ id: z12.string() });

// packages/client/src/api/doctor.ts
var doctor_default = {
  "POST /doctors": {
    schema: createDoctorSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType,
      InternalServerError
    ],
    toRequest(input) {
      return toRequest(
        "POST /doctors",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "birthDate",
            "email",
            "firstName",
            "gender",
            "lastName",
            "phoneNumber",
            "scfhsNo",
            "specialityId",
            "timezone"
          ],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /doctors": {
    schema: listDoctorsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /doctors",
        empty(input, {
          inputHeaders: [],
          inputQuery: [
            "specialityId",
            "email",
            "query",
            "page",
            "pageSize",
            "sortBy",
            "sortOrder"
          ],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /doctors/me": {
    schema: getCurrentDoctorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /doctors/me",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /doctors/me/earnings": {
    schema: getDoctorEarningsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /doctors/me/earnings",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /doctors/{id}": {
    schema: getDoctorSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /doctors/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /doctors/{id}": {
    schema: updateDoctorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /doctors/{id}",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "birthDate",
            "email",
            "firstName",
            "gender",
            "lastName",
            "phoneNumber",
            "scfhsNo",
            "specialityId",
            "timezone"
          ],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /doctors/{id}": {
    schema: deleteDoctorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /doctors/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/files.ts
import "zod";

// packages/client/src/inputs/files.ts
import { z as z14 } from "zod";
var getSignedUrlSchema = z14.object({
  name: z14.string(),
  size: z14.number().int().gt(0),
  type: z14.enum([
    "application/pdf",
    "image/png",
    "image/jpeg",
    "image/jpg",
    "application/zip",
    "application/x-zip-compressed"
  ])
});
var getFileByIdSchema = z14.object({ id: z14.string() });
var getFileAccessSchema = z14.object({
  id: z14.string(),
  purpose: z14.enum(["view", "download"])
});

// packages/client/src/api/files.ts
var files_default = {
  "POST /signed-url": {
    schema: getSignedUrlSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      PayloadTooLarge,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /signed-url",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["name", "size", "type"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /files/{id}": {
    schema: getFileByIdSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /files/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /files/{id}/access": {
    schema: getFileAccessSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /files/{id}/access",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["purpose"],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/homecare-availability.ts
import "zod";

// packages/client/src/inputs/homecare-availability.ts
import { z as z16 } from "zod";
var updateHomeCareVendorSchema = z16.object({
  timezone: z16.string().optional()
});
var getMyHomeCareAvailabilitySchema = z16.object({
  weekday: z16.enum([
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
    "SUNDAY"
  ]).optional()
});
var setHomeCareVendorAvailabilitySchema = z16.object({
  availability: z16.array(
    z16.object({
      weekday: z16.enum([
        "MONDAY",
        "TUESDAY",
        "WEDNESDAY",
        "THURSDAY",
        "FRIDAY",
        "SATURDAY",
        "SUNDAY"
      ]),
      slots: z16.array(
        z16.object({ startTime: z16.number().int(), endTime: z16.number().int() })
      )
    })
  )
});
var getMyHomeCareOverridesSchema = z16.object({}).catchall(z16.unknown());
var setHomeCareVendorOverridesSchema = z16.object({
  overrides: z16.array(
    z16.object({
      date: z16.string().datetime(),
      slots: z16.array(
        z16.object({ startTime: z16.number().int(), endTime: z16.number().int() })
      )
    })
  )
});
var getHomeCareVendorAvailabilitySchema = z16.object({
  vendorId: z16.string()
});
var getMonthlyAvailableSlotsForHomeCareVendorSchema = z16.object({
  vendorId: z16.string(),
  date: z16.string().datetime()
});
var getAvailableSlotsForHomeCareVendorSchema = z16.object({
  vendorId: z16.string(),
  date: z16.string().datetime()
});

// packages/client/src/api/homecare-availability.ts
var homecare_availability_default = {
  "PATCH /homecare/me": {
    schema: updateHomeCareVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /homecare/me",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["timezone"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /homecare/availability/me": {
    schema: getMyHomeCareAvailabilitySchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /homecare/availability/me",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["weekday"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PUT /homecare/availability/me": {
    schema: setHomeCareVendorAvailabilitySchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PUT /homecare/availability/me",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["availability"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /homecare/availability/me/overrides": {
    schema: getMyHomeCareOverridesSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /homecare/availability/me/overrides",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PUT /homecare/availability/me/overrides": {
    schema: setHomeCareVendorOverridesSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PUT /homecare/availability/me/overrides",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["overrides"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /homecare/availability/vendor/{vendorId}": {
    schema: getHomeCareVendorAvailabilitySchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /homecare/availability/vendor/{vendorId}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["vendorId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /homecare/availability/vendor/{vendorId}/monthly-slots": {
    schema: getMonthlyAvailableSlotsForHomeCareVendorSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /homecare/availability/vendor/{vendorId}/monthly-slots",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["date"],
          inputBody: [],
          inputParams: ["vendorId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /homecare/availability/vendor/{vendorId}/slots": {
    schema: getAvailableSlotsForHomeCareVendorSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /homecare/availability/vendor/{vendorId}/slots",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["date"],
          inputBody: [],
          inputParams: ["vendorId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/homecare.ts
import "zod";

// packages/client/src/inputs/homecare.ts
import { z as z18 } from "zod";
var linkHomeCareVendorSchema = z18.object({
  vendorType: z18.enum(["INDIVIDUAL", "CLINIC"])
});
var createHomeCareServiceSchema = z18.object({
  description: z18.string().optional(),
  name: z18.string()
});
var listHomeCareServicesSchema = z18.object({
  query: z18.union([z18.string(), z18.string(), z18.string()]).optional()
});
var getCurrentHomeCareVendorSchema = z18.object({}).catchall(z18.unknown());
var deleteHomeCareVendorSchema = z18.object({}).catchall(z18.unknown());
var updateHomeCareIndividualVendorSchema = z18.object({
  birthDate: z18.string().datetime().optional(),
  cityName: z18.string().optional(),
  email: z18.string().email().optional(),
  firstName: z18.string().optional(),
  gender: z18.enum(["male", "female"]).optional(),
  idOrResidencePermitFile: z18.string().uuid().optional(),
  lastName: z18.string().optional(),
  parentName: z18.string().optional(),
  phoneNumber: z18.string().optional(),
  professionalClassificationFile: z18.string().uuid().optional(),
  professionalLicenseFile: z18.string().uuid().optional(),
  yearsOfExperience: z18.number().int().min(0).optional()
});
var listHomeCareVendorsSchema = z18.object({
  vendorType: z18.enum(["INDIVIDUAL", "CLINIC"]).optional(),
  accountStatus: z18.enum(["REGISTERED", "ACTIVE", "SUSPENDED", "REJECTED"]).optional(),
  query: z18.union([z18.string(), z18.string(), z18.string()]).optional(),
  page: z18.coerce.number().int().gt(0).optional().default(1),
  pageSize: z18.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z18.enum(["email", "createdAt"]).optional().default("createdAt"),
  sortOrder: z18.enum(["asc", "desc"]).optional().default("desc")
});
var getHomeCareServiceSchema = z18.object({ id: z18.string().uuid() });
var listHomeCareVendorsForPatientsSchema = z18.object({
  vendorType: z18.enum(["INDIVIDUAL", "CLINIC"]).optional(),
  city: z18.string().optional(),
  gender: z18.enum(["male", "female"]).optional(),
  date: z18.coerce.date().optional(),
  query: z18.union([z18.string(), z18.string(), z18.string()]).optional(),
  page: z18.coerce.number().int().gt(0).optional().default(1),
  pageSize: z18.coerce.number().int().min(1).max(100).optional().default(50)
});
var getHomeCareVendorForBookingSchema = z18.object({ id: z18.string() });
var addVendorServiceSchema = z18.object({
  price: z18.number().gt(0),
  serviceId: z18.string().uuid()
});
var listVendorServicesSchema = z18.object({}).catchall(z18.unknown());
var updateVendorServicePriceSchema = z18.object({
  price: z18.number().gt(0).optional(),
  id: z18.string().uuid()
});
var removeVendorServiceSchema = z18.object({ id: z18.string().uuid() });
var createHomeCareReservationSchema = z18.object({
  description: z18.string(),
  files: z18.array(z18.string().uuid()).optional(),
  patientBirthDate: z18.string().datetime().optional(),
  patientGender: z18.enum(["male", "female"]).optional(),
  patientName: z18.string().optional(),
  scheduledDate: z18.string().datetime(),
  scheduledMinutes: z18.number().int().min(0).max(1440),
  timezone: z18.string(),
  vendorServiceId: z18.string().uuid()
});
var listAllHomeCareReservationsSchema = z18.object({
  page: z18.coerce.number().int().gt(0).optional().default(1),
  pageSize: z18.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z18.enum(["scheduledDate", "createdAt", "updatedAt", "status"]).optional().default("updatedAt"),
  sortOrder: z18.enum(["asc", "desc"]).optional().default("desc"),
  status: z18.enum(["WAITING_CONFIRMATION", "CONFIRMED", "FINISHED", "CANCELED"]).optional(),
  query: z18.union([z18.string(), z18.string(), z18.string()]).optional(),
  fromDate: z18.string().datetime().optional(),
  toDate: z18.string().datetime().optional()
});
var getHomeCareReservationsForVendorSchema = z18.object({
  page: z18.coerce.number().int().gt(0).optional().default(1),
  pageSize: z18.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z18.enum(["scheduledDate", "createdAt", "updatedAt", "status"]).optional().default("updatedAt"),
  sortOrder: z18.enum(["asc", "desc"]).optional().default("desc"),
  status: z18.enum(["WAITING_CONFIRMATION", "CONFIRMED", "FINISHED", "CANCELED"]).optional(),
  fromDate: z18.string().datetime().optional(),
  toDate: z18.string().datetime().optional()
});
var getHomeCareReservationByIdSchema = z18.object({
  id: z18.string().uuid()
});
var confirmHomeCareReservationSchema = z18.object({
  reservationId: z18.string().uuid()
});
var finishHomeCareReservationByVendorSchema = z18.object({
  notes: z18.string().optional(),
  reservationId: z18.string().uuid()
});
var finishHomeCareReservationByPatientSchema = z18.object({
  id: z18.string().uuid()
});
var patientCancelHomeCareReservationSchema = z18.object({
  id: z18.string().uuid()
});
var getHomeCareReservationStatsSchema = z18.object({
  dates: z18.array(z18.string().datetime())
});
var getHomeCareReservationsForPatientSchema = z18.object({
  status: z18.enum(["WAITING_CONFIRMATION", "CONFIRMED", "FINISHED", "CANCELED"]).optional(),
  page: z18.coerce.number().int().gt(0).optional().default(1),
  pageSize: z18.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z18.enum(["scheduledDate", "createdAt", "updatedAt"]).optional().default("createdAt"),
  sortOrder: z18.enum(["asc", "desc"]).optional().default("desc")
});
var getHomeCareReservationForPatientByIdSchema = z18.object({
  id: z18.string().uuid()
});
var activateHomeCareVendorSchema = z18.object({ id: z18.string() });
var rejectHomeCareVendorSchema = z18.object({
  reason: z18.string().optional(),
  id: z18.string()
});
var suspendHomeCareVendorSchema = z18.object({
  reason: z18.string().optional(),
  id: z18.string()
});
var unsuspendHomeCareVendorSchema = z18.object({ id: z18.string() });
var getCurrentHomeCareClinicVendorSchema = z18.object({}).catchall(z18.unknown());
var updateHomeCareClinicVendorSchema = z18.object({
  cbahiAccreditationFile: z18.string().uuid().optional(),
  cityName: z18.string().optional(),
  clinicName: z18.string().optional(),
  commercialRegistrationFile: z18.string().uuid().optional(),
  contactFirstName: z18.string().optional(),
  contactLastName: z18.string().optional(),
  contactPhoneNumber: z18.string().optional(),
  email: z18.string().email().optional(),
  logoFile: z18.string().uuid().optional(),
  mohHomeCareeLicenseFile: z18.string().uuid().optional(),
  nationalAddressCertificateFile: z18.string().uuid().optional(),
  phoneNumber: z18.string().optional(),
  practitionerLicensesFile: z18.string().uuid().optional()
});

// packages/client/src/api/homecare.ts
var homecare_default = {
  "POST /homecare/link": {
    schema: linkHomeCareVendorSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /homecare/link",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["vendorType"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /homecare/services": {
    schema: createHomeCareServiceSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /homecare/services",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["description", "name"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /homecare/services": {
    schema: listHomeCareServicesSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /homecare/services",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["query"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /homecare/me": {
    schema: getCurrentHomeCareVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /homecare/me",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /homecare/me": {
    schema: deleteHomeCareVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /homecare/me",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /homecare/individual/me": {
    schema: updateHomeCareIndividualVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /homecare/individual/me",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "birthDate",
            "cityName",
            "email",
            "firstName",
            "gender",
            "idOrResidencePermitFile",
            "lastName",
            "parentName",
            "phoneNumber",
            "professionalClassificationFile",
            "professionalLicenseFile",
            "yearsOfExperience"
          ],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /homecare": {
    schema: listHomeCareVendorsSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /homecare",
        empty(input, {
          inputHeaders: [],
          inputQuery: [
            "vendorType",
            "accountStatus",
            "query",
            "page",
            "pageSize",
            "sortBy",
            "sortOrder"
          ],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /homecare/services/{id}": {
    schema: getHomeCareServiceSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /homecare/services/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /homecare/vendors/browse": {
    schema: listHomeCareVendorsForPatientsSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /homecare/vendors/browse",
        empty(input, {
          inputHeaders: [],
          inputQuery: [
            "vendorType",
            "city",
            "gender",
            "date",
            "query",
            "page",
            "pageSize"
          ],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /homecare/vendors/{id}": {
    schema: getHomeCareVendorForBookingSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /homecare/vendors/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /homecare/me/services": {
    schema: addVendorServiceSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /homecare/me/services",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["price", "serviceId"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /homecare/me/services": {
    schema: listVendorServicesSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /homecare/me/services",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /homecare/me/services/{id}": {
    schema: updateVendorServicePriceSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /homecare/me/services/{id}",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["price"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /homecare/me/services/{id}": {
    schema: removeVendorServiceSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /homecare/me/services/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /homecare/reservations": {
    schema: createHomeCareReservationSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /homecare/reservations",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "description",
            "files",
            "patientBirthDate",
            "patientGender",
            "patientName",
            "scheduledDate",
            "scheduledMinutes",
            "timezone",
            "vendorServiceId"
          ],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /homecare/all-reservations": {
    schema: listAllHomeCareReservationsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /homecare/all-reservations",
        empty(input, {
          inputHeaders: [],
          inputQuery: [
            "page",
            "pageSize",
            "sortBy",
            "sortOrder",
            "status",
            "query",
            "fromDate",
            "toDate"
          ],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /homecare/me/reservations": {
    schema: getHomeCareReservationsForVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /homecare/me/reservations",
        empty(input, {
          inputHeaders: [],
          inputQuery: [
            "page",
            "pageSize",
            "sortBy",
            "sortOrder",
            "status",
            "fromDate",
            "toDate"
          ],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /homecare/reservations/{id}": {
    schema: getHomeCareReservationByIdSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /homecare/reservations/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /homecare/reservations/me/confirm": {
    schema: confirmHomeCareReservationSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /homecare/reservations/me/confirm",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["reservationId"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /homecare/reservations/me/finish": {
    schema: finishHomeCareReservationByVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /homecare/reservations/me/finish",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["notes", "reservationId"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /homecare/patient/reservations/{id}/finish": {
    schema: finishHomeCareReservationByPatientSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /homecare/patient/reservations/{id}/finish",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /homecare/patient/reservations/{id}/cancel": {
    schema: patientCancelHomeCareReservationSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /homecare/patient/reservations/{id}/cancel",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /homecare/me/reservations/stats": {
    schema: getHomeCareReservationStatsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /homecare/me/reservations/stats",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["dates"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /patients/me/homecare-reservations": {
    schema: getHomeCareReservationsForPatientSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /patients/me/homecare-reservations",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["status", "page", "pageSize", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /patients/me/homecare-reservations/{id}": {
    schema: getHomeCareReservationForPatientByIdSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /patients/me/homecare-reservations/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /homecare/{id}/activate": {
    schema: activateHomeCareVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /homecare/{id}/activate",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /homecare/{id}/reject": {
    schema: rejectHomeCareVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /homecare/{id}/reject",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["reason"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /homecare/{id}/suspend": {
    schema: suspendHomeCareVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /homecare/{id}/suspend",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["reason"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /homecare/{id}/unsuspend": {
    schema: unsuspendHomeCareVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /homecare/{id}/unsuspend",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /homecare/clinic/me": {
    schema: getCurrentHomeCareClinicVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /homecare/clinic/me",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /homecare/clinic/me": {
    schema: updateHomeCareClinicVendorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /homecare/clinic/me",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "cbahiAccreditationFile",
            "cityName",
            "clinicName",
            "commercialRegistrationFile",
            "contactFirstName",
            "contactLastName",
            "contactPhoneNumber",
            "email",
            "logoFile",
            "mohHomeCareeLicenseFile",
            "nationalAddressCertificateFile",
            "phoneNumber",
            "practitionerLicensesFile"
          ],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/icd10.ts
import "zod";

// packages/client/src/inputs/icd10.ts
import { z as z20 } from "zod";
var getIcd10CodesSchema = z20.object({
  query: z20.union([z20.string(), z20.string(), z20.string()]).optional(),
  page: z20.coerce.number().int().gt(0).optional().default(1),
  pageSize: z20.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z20.enum(["code", "description"]).optional().default("code"),
  sortOrder: z20.enum(["asc", "desc"]).optional().default("asc")
});
var getIcd10CodeByIdSchema = z20.object({ id: z20.string().uuid() });

// packages/client/src/api/icd10.ts
var icd10_default = {
  "GET /icd10codes": {
    schema: getIcd10CodesSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /icd10codes",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["query", "page", "pageSize", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /icd10codes/{id}": {
    schema: getIcd10CodeByIdSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /icd10codes/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/ingest.ts
import "zod";

// packages/client/src/inputs/ingest.ts
import { z as z22 } from "zod";
var postEvlogingestSchema = z22.object({
  level: z22.enum(["info", "error", "warn", "debug"]),
  service: z22.string(),
  timestamp: z22.string()
});

// packages/client/src/api/ingest.ts
var ingest_default = {
  "POST /evlog/ingest": {
    schema: postEvlogingestSchema,
    output: [
      NoContent,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /evlog/ingest",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["level", "service", "timestamp"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/labtest.ts
import "zod";

// packages/client/src/inputs/labtest.ts
import { z as z24 } from "zod";
var getLabTestsSchema = z24.object({
  query: z24.union([z24.string(), z24.string(), z24.string()]).optional(),
  page: z24.coerce.number().int().gt(0).optional().default(1),
  pageSize: z24.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z24.enum(["name", "loincNum"]).optional().default("name"),
  sortOrder: z24.enum(["asc", "desc"]).optional().default("asc")
});
var getLabTestByIdSchema = z24.object({ id: z24.string().uuid() });

// packages/client/src/api/labtest.ts
var labtest_default = {
  "GET /labtests": {
    schema: getLabTestsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /labtests",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["query", "page", "pageSize", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /labtests/{id}": {
    schema: getLabTestByIdSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /labtests/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/medication.ts
import "zod";

// packages/client/src/inputs/medication.ts
import { z as z26 } from "zod";
var getMedicationsSchema = z26.object({
  query: z26.union([z26.string(), z26.string(), z26.string()]).optional(),
  page: z26.coerce.number().int().gt(0).optional().default(1),
  pageSize: z26.coerce.number().int().min(1).max(100).optional().default(50)
});

// packages/client/src/api/medication.ts
var medication_default = {
  "GET /medications": {
    schema: getMedicationsSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /medications",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["query", "page", "pageSize"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/meeting.ts
import "zod";

// packages/client/src/inputs/meeting.ts
import { z as z28 } from "zod";
var getMeetingsSchema = z28.object({}).catchall(z28.unknown());
var joinMeetingSchema = z28.object({ reservationId: z28.string() });
var joinMeetingAsGuestSchema = z28.object({ token: z28.string() });

// packages/client/src/api/meeting.ts
var meeting_default = {
  "GET /meetings": {
    schema: getMeetingsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /meetings",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /meetings/reservations/{reservationId}/join": {
    schema: joinMeetingSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /meetings/reservations/{reservationId}/join",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["reservationId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /meetings/guest/join": {
    schema: joinMeetingAsGuestSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      NotFound,
      Gone,
      UnsupportedMediaType,
      APIResponse
    ],
    toRequest(input) {
      return toRequest(
        "POST /meetings/guest/join",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["token"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/notifications.ts
import "zod";

// packages/client/src/inputs/notifications.ts
import { z as z30 } from "zod";
var listNotificationsSchema = z30.object({
  read: z30.union([
    z30.string().optional().default("false"),
    z30.boolean().optional().default(false)
  ]).optional(),
  page: z30.coerce.number().int().gt(0).optional().default(1),
  pageSize: z30.coerce.number().int().min(1).max(100).optional().default(50)
});
var markNotificationAsReadSchema = z30.object({ id: z30.string().uuid() });
var markAllNotificationsAsReadSchema = z30.object({}).catchall(z30.unknown());
var registerPushTokenSchema = z30.object({ token: z30.string() });
var removePushTokenSchema = z30.object({ token: z30.string() });

// packages/client/src/api/notifications.ts
var notifications_default = {
  "GET /notifications": {
    schema: listNotificationsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /notifications",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["read", "page", "pageSize"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PUT /notifications/{id}/read": {
    schema: markNotificationAsReadSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PUT /notifications/{id}/read",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PUT /notifications/mark-all-read": {
    schema: markAllNotificationsAsReadSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PUT /notifications/mark-all-read",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /notifications/push-tokens": {
    schema: registerPushTokenSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /notifications/push-tokens",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["token"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /notifications/push-tokens": {
    schema: removePushTokenSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /notifications/push-tokens",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["token"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/payments.ts
import "zod";

// packages/client/src/inputs/payments.ts
import { z as z32 } from "zod";
var listStandardPaymentsSchema = z32.object({
  status: z32.enum([
    "PENDING",
    "PAID",
    "FAILED",
    "VOIDED",
    "AUTHORIZED",
    "CAPTURED",
    "ABANDONED",
    "VERIFIED",
    "CANCELED",
    "EXPIRED",
    "REFUNDED",
    "REFUND_PENDING"
  ]).optional(),
  query: z32.union([z32.string(), z32.string(), z32.string()]).optional(),
  page: z32.coerce.number().int().gt(0).optional().default(1),
  pageSize: z32.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z32.string().optional().default("createdAt"),
  sortOrder: z32.enum(["asc", "desc"]).optional().default("desc")
});
var listSecondOpinionPaymentsSchema = z32.object({
  status: z32.enum([
    "PENDING",
    "PAID",
    "FAILED",
    "VOIDED",
    "AUTHORIZED",
    "CAPTURED",
    "ABANDONED",
    "VERIFIED",
    "CANCELED",
    "EXPIRED",
    "REFUNDED",
    "REFUND_PENDING"
  ]).optional(),
  query: z32.union([z32.string(), z32.string(), z32.string()]).optional(),
  page: z32.coerce.number().int().gt(0).optional().default(1),
  pageSize: z32.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z32.string().optional().default("createdAt"),
  sortOrder: z32.enum(["asc", "desc"]).optional().default("desc")
});
var listHomecarePaymentsSchema = z32.object({
  status: z32.enum([
    "PENDING",
    "PAID",
    "FAILED",
    "VOIDED",
    "AUTHORIZED",
    "CAPTURED",
    "ABANDONED",
    "VERIFIED",
    "CANCELED",
    "EXPIRED",
    "REFUNDED",
    "REFUND_PENDING"
  ]).optional(),
  query: z32.union([z32.string(), z32.string(), z32.string()]).optional(),
  page: z32.coerce.number().int().gt(0).optional().default(1),
  pageSize: z32.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z32.string().optional().default("createdAt"),
  sortOrder: z32.enum(["asc", "desc"]).optional().default("desc")
});
var createPaymentSchema = z32.object({
  amount: z32.number().int().gt(0),
  moyasarId: z32.string()
});
var verifyPaymentMobileSchema = z32.object({ moyasarId: z32.string() });

// packages/client/src/api/payments.ts
var payments_default = {
  "GET /payments/standard/list": {
    schema: listStandardPaymentsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /payments/standard/list",
        empty(input, {
          inputHeaders: [],
          inputQuery: [
            "status",
            "query",
            "page",
            "pageSize",
            "sortBy",
            "sortOrder"
          ],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /payments/second-opinion/list": {
    schema: listSecondOpinionPaymentsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /payments/second-opinion/list",
        empty(input, {
          inputHeaders: [],
          inputQuery: [
            "status",
            "query",
            "page",
            "pageSize",
            "sortBy",
            "sortOrder"
          ],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /payments/homecare/list": {
    schema: listHomecarePaymentsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /payments/homecare/list",
        empty(input, {
          inputHeaders: [],
          inputQuery: [
            "status",
            "query",
            "page",
            "pageSize",
            "sortBy",
            "sortOrder"
          ],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /payments": {
    schema: createPaymentSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /payments",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["amount", "moyasarId"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /payments/verify": {
    schema: verifyPaymentMobileSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      NotFound,
      Conflict,
      UnsupportedMediaType,
      InternalServerError,
      BadGateway
    ],
    toRequest(input) {
      return toRequest(
        "POST /payments/verify",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["moyasarId"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/printing-request.ts
import "zod";

// packages/client/src/inputs/printing-request.ts
import { z as z34 } from "zod";
var createB2BPrintingRequestSchema = z34.object({
  description: z34.string(),
  files: z34.array(z34.string().uuid()).optional()
});
var listPrintingRequestsSchema = z34.object({
  status: z34.enum([
    "WAITING_DOCTOR_ASSIGNMENT",
    "WAITING_DOCTOR_CONFIRMATION",
    "CONFIRMED",
    "FINISHED"
  ]).optional(),
  query: z34.union([z34.string(), z34.string(), z34.string()]).optional(),
  page: z34.coerce.number().int().gt(0).optional().default(1),
  pageSize: z34.coerce.number().int().min(1).max(100).optional().default(50)
});
var getPrintingRequestByIdSchema = z34.object({ id: z34.string().uuid() });
var getAvailableDoctorsForPrintingRequestSchema = z34.object({
  id: z34.string().uuid()
});
var assignDoctorToPrintingRequestSchema = z34.object({
  doctorId: z34.string().optional(),
  id: z34.string().uuid()
});
var confirmPrintingRequestSchema = z34.object({ id: z34.string().uuid() });
var attachPrintingLinkSchema = z34.object({
  downloadPassword: z34.string().optional(),
  downloadUrl: z34.string().url(),
  id: z34.string().uuid()
});
var referPrintingRequestSchema = z34.object({
  reason: z34.string().optional(),
  id: z34.string().uuid()
});
var getPrintingRequestsForDoctorSchema = z34.object({
  status: z34.enum([
    "WAITING_DOCTOR_ASSIGNMENT",
    "WAITING_DOCTOR_CONFIRMATION",
    "CONFIRMED",
    "FINISHED"
  ]).optional(),
  page: z34.coerce.number().int().gt(0).optional().default(1),
  pageSize: z34.coerce.number().int().min(1).max(100).optional().default(50)
});
var getPrintingRequestsForPatientSchema = z34.object({
  status: z34.enum([
    "WAITING_DOCTOR_ASSIGNMENT",
    "WAITING_DOCTOR_CONFIRMATION",
    "CONFIRMED",
    "FINISHED"
  ]).optional(),
  page: z34.coerce.number().int().gt(0).optional().default(1),
  pageSize: z34.coerce.number().int().min(1).max(100).optional().default(50)
});

// packages/client/src/api/printing-request.ts
var printing_request_default = {
  "POST /printing-requests/b2b": {
    schema: createB2BPrintingRequestSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /printing-requests/b2b",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["description", "files"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /printing-requests": {
    schema: listPrintingRequestsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /printing-requests",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["status", "query", "page", "pageSize"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /printing-requests/{id}": {
    schema: getPrintingRequestByIdSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /printing-requests/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /printing-requests/{id}/doctors": {
    schema: getAvailableDoctorsForPrintingRequestSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /printing-requests/{id}/doctors",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /printing-requests/{id}/assign-doctor": {
    schema: assignDoctorToPrintingRequestSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /printing-requests/{id}/assign-doctor",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["doctorId"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /printing-requests/{id}/confirm": {
    schema: confirmPrintingRequestSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /printing-requests/{id}/confirm",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /printing-requests/{id}/attach-link": {
    schema: attachPrintingLinkSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /printing-requests/{id}/attach-link",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["downloadPassword", "downloadUrl"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /printing-requests/{id}/refer": {
    schema: referPrintingRequestSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /printing-requests/{id}/refer",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["reason"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /doctors/me/printing-requests": {
    schema: getPrintingRequestsForDoctorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /doctors/me/printing-requests",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["status", "page", "pageSize"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /patients/me/printing-requests": {
    schema: getPrintingRequestsForPatientSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /patients/me/printing-requests",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["status", "page", "pageSize"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/promo-code.ts
import "zod";

// packages/client/src/inputs/promo-code.ts
import { z as z36 } from "zod";
var validatePromoCodeSchema = z36.object({
  code: z36.string(),
  service: z36.enum(["standard", "second_opinion"]),
  urgent: z36.boolean().optional().default(false)
});
var getPromoCodesSchema = z36.object({
  query: z36.union([z36.string(), z36.string(), z36.string()]).optional(),
  page: z36.coerce.number().int().gt(0).optional().default(1),
  pageSize: z36.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z36.enum(["code", "validUntil", "validFrom", "usedCount", "createdAt"]).optional().default("createdAt"),
  sortOrder: z36.enum(["asc", "desc"]).optional().default("desc")
});
var createPromoCodeSchema = z36.object({
  code: z36.string(),
  discountPercent: z36.number().int().min(1).max(100),
  maxUses: z36.number().int().min(1),
  validFrom: z36.string().datetime(),
  validUntil: z36.string().datetime()
});
var getPromoCodeSchema = z36.object({ id: z36.string() });
var updatePromoCodeSchema = z36.object({
  discountPercent: z36.number().int().min(1).max(100).optional(),
  maxUses: z36.number().int().min(1).optional(),
  validFrom: z36.string().datetime().optional(),
  validUntil: z36.string().datetime().optional(),
  id: z36.string()
});
var deletePromoCodeSchema = z36.object({ id: z36.string() });

// packages/client/src/api/promo-code.ts
var promo_code_default = {
  "POST /promo-codes/validate": {
    schema: validatePromoCodeSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      NotFound,
      UnsupportedMediaType,
      InternalServerError
    ],
    toRequest(input) {
      return toRequest(
        "POST /promo-codes/validate",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["code", "service", "urgent"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /promo-codes": {
    schema: getPromoCodesSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /promo-codes",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["query", "page", "pageSize", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /promo-codes": {
    schema: createPromoCodeSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /promo-codes",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "code",
            "discountPercent",
            "maxUses",
            "validFrom",
            "validUntil"
          ],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /promo-codes/{id}": {
    schema: getPromoCodeSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /promo-codes/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /promo-codes/{id}": {
    schema: updatePromoCodeSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /promo-codes/{id}",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["discountPercent", "maxUses", "validFrom", "validUntil"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /promo-codes/{id}": {
    schema: deletePromoCodeSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /promo-codes/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/report.ts
import "zod";

// packages/client/src/inputs/report.ts
import { z as z38 } from "zod";
var getReservationReportPdfSchema = z38.object({
  id: z38.string().uuid()
});
var getReservationPrescriptionPdfSchema = z38.object({
  id: z38.string().uuid()
});
var getReservationLabtestsPdfSchema = z38.object({
  id: z38.string().uuid()
});
var getSecondOpinionReportPdfSchema = z38.object({
  id: z38.string().uuid()
});
var getSickLeaveCertificatePdfSchema = z38.object({
  id: z38.string().uuid()
});
var getDiagnosisReportPdfSchema = z38.object({ id: z38.string().uuid() });
var getDiagnosisPrescriptionPdfSchema = z38.object({
  id: z38.string().uuid()
});
var getDiagnosisLabtestsPdfSchema = z38.object({
  id: z38.string().uuid()
});
var getDiagnosisSickLeavePdfSchema = z38.object({
  id: z38.string().uuid()
});

// packages/client/src/api/report.ts
var report_default = {
  "GET /reservations/{id}/report.pdf": {
    schema: getReservationReportPdfSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /reservations/{id}/report.pdf",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /reservations/{id}/prescription.pdf": {
    schema: getReservationPrescriptionPdfSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /reservations/{id}/prescription.pdf",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /reservations/{id}/labtests.pdf": {
    schema: getReservationLabtestsPdfSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /reservations/{id}/labtests.pdf",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /second-opinions/{id}/report.pdf": {
    schema: getSecondOpinionReportPdfSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /second-opinions/{id}/report.pdf",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /reservations/{id}/sick-leave.pdf": {
    schema: getSickLeaveCertificatePdfSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      NotFound,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /reservations/{id}/sick-leave.pdf",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /diagnosis/{id}/report.pdf": {
    schema: getDiagnosisReportPdfSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /diagnosis/{id}/report.pdf",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /diagnosis/{id}/prescription.pdf": {
    schema: getDiagnosisPrescriptionPdfSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /diagnosis/{id}/prescription.pdf",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /diagnosis/{id}/labtests.pdf": {
    schema: getDiagnosisLabtestsPdfSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /diagnosis/{id}/labtests.pdf",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /diagnosis/{id}/sick-leave.pdf": {
    schema: getDiagnosisSickLeavePdfSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      NotFound,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /diagnosis/{id}/sick-leave.pdf",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/reservation.ts
import "zod";

// packages/client/src/inputs/reservation.ts
import { z as z40 } from "zod";
var getAllReservationsSchema = z40.object({
  status: z40.enum([
    "WAITING_DOCTOR_ASSIGNMENT",
    "WAITING_DOCTOR_CONFIRMATION",
    "CONFIRMED",
    "FINISHED",
    "CANCELED",
    "AVAILABLE_FOR_FOLLOWUP",
    "EXPIRED"
  ]).optional(),
  query: z40.union([z40.string(), z40.string(), z40.string()]).optional(),
  page: z40.coerce.number().int().gt(0).optional().default(1),
  pageSize: z40.coerce.number().int().min(1).max(100).optional().default(50)
});
var getReservationByIdSchema = z40.object({ id: z40.string().uuid() });
var getReservationDiagnosesSchema = z40.object({
  id: z40.string().uuid()
});
var checkReservationAvailabilitySchema = z40.object({
  scheduledDate: z40.string().datetime(),
  scheduledMinutes: z40.coerce.number().int(),
  specialityId: z40.string().uuid()
});
var createB2BAppointmentSchema = z40.object({
  description: z40.string(),
  doctorName: z40.string(),
  files: z40.array(z40.string().uuid()).optional(),
  guestEmail: z40.string().email(),
  patientBirthDate: z40.string().datetime().optional(),
  patientGender: z40.enum(["male", "female"]).optional(),
  patientName: z40.string().optional(),
  scheduledDate: z40.string().datetime(),
  scheduledMinutes: z40.coerce.number().int(),
  specialityId: z40.string().uuid(),
  timezone: z40.string()
});
var getAvailableDoctorsForReservationSchema = z40.object({
  reservationId: z40.string().uuid()
});
var assignDoctorToReservationSchema = z40.object({
  doctorId: z40.string().optional(),
  specialityId: z40.string().uuid().optional(),
  reservationId: z40.string().uuid()
});
var getReservationsForDoctorSchema = z40.object({
  status: z40.enum([
    "WAITING_DOCTOR_ASSIGNMENT",
    "WAITING_DOCTOR_CONFIRMATION",
    "CONFIRMED",
    "FINISHED",
    "CANCELED",
    "AVAILABLE_FOR_FOLLOWUP",
    "EXPIRED"
  ]).optional(),
  query: z40.union([z40.string(), z40.string(), z40.string()]).optional(),
  startDate: z40.string().datetime().optional(),
  endDate: z40.string().datetime().optional(),
  page: z40.coerce.number().int().gt(0).optional().default(1),
  pageSize: z40.coerce.number().int().min(1).max(100).optional().default(50)
});
var getReservationsForPatientSchema = z40.object({
  query: z40.union([z40.string(), z40.string(), z40.string()]).optional(),
  page: z40.coerce.number().int().gt(0).optional().default(1),
  pageSize: z40.coerce.number().int().min(1).max(100).optional().default(50)
});
var getDoctorReservationStatsSchema = z40.object({
  dates: z40.array(z40.string().datetime())
});
var confirmReservationSchema = z40.object({
  reservationId: z40.string().uuid()
});
var getRefundEligibilitySchema = z40.object({ id: z40.string().uuid() });
var cancelReservationSchema = z40.object({ id: z40.string().uuid() });
var scheduleFollowupSchema = z40.object({
  scheduledDate: z40.string().datetime().optional(),
  scheduledMinutes: z40.coerce.number().int(),
  id: z40.string().uuid()
});
var rejectFollowupTimeSchema = z40.object({
  reason: z40.string().optional(),
  id: z40.string().uuid()
});
var referToAnotherDoctorSchema = z40.object({
  newSpecialityId: z40.string().uuid().optional(),
  reason: z40.string(),
  id: z40.string().uuid()
});
var requestMoreInfoSchema = z40.object({
  requestedInfo: z40.string().optional(),
  id: z40.string().uuid()
});
var submitAdditionalInfoSchema = z40.object({
  details: z40.string().optional(),
  files: z40.array(z40.string().uuid()).optional(),
  id: z40.string().uuid()
});

// packages/client/src/api/reservation.ts
var reservation_default = {
  "GET /reservations": {
    schema: getAllReservationsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /reservations",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["status", "query", "page", "pageSize"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /reservations/{id}": {
    schema: getReservationByIdSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /reservations/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /reservations/{id}/diagnoses": {
    schema: getReservationDiagnosesSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /reservations/{id}/diagnoses",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /reservations/check-availability": {
    schema: checkReservationAvailabilitySchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /reservations/check-availability",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["scheduledDate", "scheduledMinutes", "specialityId"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /appointment/b2b": {
    schema: createB2BAppointmentSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /appointment/b2b",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "description",
            "doctorName",
            "files",
            "guestEmail",
            "patientBirthDate",
            "patientGender",
            "patientName",
            "scheduledDate",
            "scheduledMinutes",
            "specialityId",
            "timezone"
          ],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /reservations/{reservationId}/availability/doctors": {
    schema: getAvailableDoctorsForReservationSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /reservations/{reservationId}/availability/doctors",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["reservationId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /reservations/{reservationId}/assign-doctor": {
    schema: assignDoctorToReservationSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /reservations/{reservationId}/assign-doctor",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["doctorId", "specialityId"],
          inputParams: ["reservationId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /doctors/me/reservations": {
    schema: getReservationsForDoctorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /doctors/me/reservations",
        empty(input, {
          inputHeaders: [],
          inputQuery: [
            "status",
            "query",
            "startDate",
            "endDate",
            "page",
            "pageSize"
          ],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /patients/me/reservations": {
    schema: getReservationsForPatientSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /patients/me/reservations",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["query", "page", "pageSize"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /doctors/me/reservations/stats": {
    schema: getDoctorReservationStatsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /doctors/me/reservations/stats",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["dates"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /reservations/me/confirm": {
    schema: confirmReservationSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /reservations/me/confirm",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["reservationId"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /reservations/{id}/refund-eligibility": {
    schema: getRefundEligibilitySchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /reservations/{id}/refund-eligibility",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /reservations/{id}/cancel": {
    schema: cancelReservationSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /reservations/{id}/cancel",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /reservations/{id}/schedule-followup": {
    schema: scheduleFollowupSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /reservations/{id}/schedule-followup",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["scheduledDate", "scheduledMinutes"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /reservations/{id}/reject-followup-time": {
    schema: rejectFollowupTimeSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /reservations/{id}/reject-followup-time",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["reason"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /reservations/{id}/refer": {
    schema: referToAnotherDoctorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /reservations/{id}/refer",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["newSpecialityId", "reason"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /reservations/{id}/request-info": {
    schema: requestMoreInfoSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /reservations/{id}/request-info",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["requestedInfo"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /reservations/{id}/submit-info": {
    schema: submitAdditionalInfoSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /reservations/{id}/submit-info",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["details", "files"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/review.ts
import "zod";

// packages/client/src/inputs/review.ts
import { z as z42 } from "zod";
var getReviewCategoriesSchema = z42.object({}).catchall(z42.unknown());
var createMeetingReviewSchema = z42.object({
  ratings: z42.array(
    z42.object({
      categoryId: z42.string().uuid(),
      rating: z42.number().int().min(1).max(5)
    })
  ).optional(),
  review: z42.string().optional(),
  meetingId: z42.string().uuid()
});
var getMeetingReviewSchema = z42.object({
  meetingId: z42.string().uuid()
});
var getReservationReviewsSchema = z42.object({
  reservationId: z42.string().uuid()
});

// packages/client/src/api/review.ts
var review_default = {
  "GET /review-categories": {
    schema: getReviewCategoriesSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /review-categories",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /meetings/{meetingId}/review": {
    schema: createMeetingReviewSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      NotFound,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /meetings/{meetingId}/review",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["ratings", "review"],
          inputParams: ["meetingId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /meetings/{meetingId}/review": {
    schema: getMeetingReviewSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      NotFound,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /meetings/{meetingId}/review",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["meetingId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /reservations/{reservationId}/reviews": {
    schema: getReservationReviewsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /reservations/{reservationId}/reviews",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["reservationId"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/second-opinion.ts
import "zod";

// packages/client/src/inputs/second-opinion.ts
import { z as z44 } from "zod";
var getAllSecondOpinionsSchema = z44.object({
  status: z44.enum([
    "WAITING_DOCTOR_ASSIGNMENT",
    "WAITING_DOCTOR_CONFIRMATION",
    "CONFIRMED",
    "FINISHED"
  ]).optional(),
  query: z44.union([z44.string(), z44.string(), z44.string()]).optional(),
  page: z44.coerce.number().int().gt(0).optional().default(1),
  pageSize: z44.coerce.number().int().min(1).max(100).optional().default(50)
});
var createB2BSecondOpinionSchema = z44.object({
  description: z44.string(),
  doctorName: z44.string(),
  files: z44.array(z44.string().uuid()).optional(),
  patientBirthDate: z44.string().datetime().optional(),
  patientGender: z44.enum(["male", "female"]).optional(),
  patientName: z44.string().optional(),
  specialityId: z44.string().uuid(),
  urgent: z44.boolean().default(false)
});
var getSecondOpinionByIdSchema = z44.object({ id: z44.string().uuid() });
var getAvailableDoctorsForSecondOpinionSchema = z44.object({
  id: z44.string().uuid()
});
var assignDoctorToSecondOpinionSchema = z44.object({
  doctorId: z44.string().optional(),
  specialityId: z44.string().uuid().optional(),
  id: z44.string().uuid()
});
var confirmSecondOpinionSchema = z44.object({ id: z44.string().uuid() });
var requestMoreInfoSecondOpinionSchema = z44.object({
  requestedInfo: z44.string().optional(),
  id: z44.string().uuid()
});
var submitInfoSecondOpinionSchema = z44.object({
  details: z44.string().optional(),
  files: z44.array(z44.string().uuid()).optional(),
  id: z44.string().uuid()
});
var attachReportSchema = z44.object({
  files: z44.array(z44.string().uuid()).optional(),
  notes: z44.string().optional(),
  id: z44.string().uuid()
});
var getSecondOpinionsForDoctorSchema = z44.object({
  status: z44.enum([
    "WAITING_DOCTOR_ASSIGNMENT",
    "WAITING_DOCTOR_CONFIRMATION",
    "CONFIRMED",
    "FINISHED"
  ]).optional(),
  page: z44.coerce.number().int().gt(0).optional().default(1),
  pageSize: z44.coerce.number().int().min(1).max(100).optional().default(50),
  sortOrder: z44.enum(["asc", "desc"]).optional().default("asc")
});
var getSecondOpinionsForPatientSchema = z44.object({
  status: z44.enum([
    "WAITING_DOCTOR_ASSIGNMENT",
    "WAITING_DOCTOR_CONFIRMATION",
    "CONFIRMED",
    "FINISHED"
  ]).optional(),
  page: z44.coerce.number().int().gt(0).optional().default(1),
  pageSize: z44.coerce.number().int().min(1).max(100).optional().default(50)
});
var referSecondOpinionToAnotherDoctorSchema = z44.object({
  newSpecialityId: z44.string().uuid().optional(),
  reason: z44.string(),
  id: z44.string().uuid()
});

// packages/client/src/api/second-opinion.ts
var second_opinion_default = {
  "GET /second-opinions": {
    schema: getAllSecondOpinionsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /second-opinions",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["status", "query", "page", "pageSize"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /second-opinion/b2b": {
    schema: createB2BSecondOpinionSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /second-opinion/b2b",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "description",
            "doctorName",
            "files",
            "patientBirthDate",
            "patientGender",
            "patientName",
            "specialityId",
            "urgent"
          ],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /second-opinions/{id}": {
    schema: getSecondOpinionByIdSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /second-opinions/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /second-opinions/{id}/doctors": {
    schema: getAvailableDoctorsForSecondOpinionSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /second-opinions/{id}/doctors",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /second-opinions/{id}/assign-doctor": {
    schema: assignDoctorToSecondOpinionSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /second-opinions/{id}/assign-doctor",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["doctorId", "specialityId"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /second-opinions/{id}/confirm": {
    schema: confirmSecondOpinionSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /second-opinions/{id}/confirm",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /second-opinions/{id}/request-info": {
    schema: requestMoreInfoSecondOpinionSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /second-opinions/{id}/request-info",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["requestedInfo"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /second-opinions/{id}/submit-info": {
    schema: submitInfoSecondOpinionSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /second-opinions/{id}/submit-info",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["details", "files"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /second-opinions/{id}/attach-report": {
    schema: attachReportSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /second-opinions/{id}/attach-report",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["files", "notes"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /doctors/me/second-opinions": {
    schema: getSecondOpinionsForDoctorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /doctors/me/second-opinions",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["status", "page", "pageSize", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /patients/me/second-opinions": {
    schema: getSecondOpinionsForPatientSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /patients/me/second-opinions",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["status", "page", "pageSize"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /second-opinions/{id}/refer": {
    schema: referSecondOpinionToAnotherDoctorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /second-opinions/{id}/refer",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["newSpecialityId", "reason"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/settings.ts
import "zod";

// packages/client/src/inputs/settings.ts
import { z as z46 } from "zod";
var getSettingsSchema = z46.object({
  key: z46.string().optional(),
  section: z46.string().optional(),
  isActive: z46.union([z46.string().optional(), z46.boolean().optional()]).optional()
});
var createSettingSchema = z46.object({
  description: z46.string().optional(),
  isActive: z46.boolean().default(true),
  key: z46.string(),
  section: z46.string().default("general"),
  value: z46.string()
});
var updateSettingSchema = z46.object({
  description: z46.string().optional(),
  isActive: z46.boolean().optional(),
  key: z46.string().optional(),
  section: z46.string().optional(),
  value: z46.string().optional(),
  oldSection: z46.string(),
  oldKey: z46.string()
});
var deleteSettingSchema = z46.object({
  section: z46.string(),
  key: z46.string()
});
var getPublicSettingsSchema = z46.object({
  section: z46.string().optional()
});
var bulkUpdateSettingsSchema = z46.object({
  settings: z46.array(
    z46.object({
      key: z46.string(),
      value: z46.string(),
      description: z46.string().optional(),
      section: z46.string().optional(),
      isActive: z46.union([z46.string().optional(), z46.boolean().optional()]).optional()
    })
  ).optional()
});

// packages/client/src/api/settings.ts
var settings_default = {
  "GET /settings": {
    schema: getSettingsSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /settings",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["key", "section", "isActive"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /settings": {
    schema: createSettingSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /settings",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["description", "isActive", "key", "section", "value"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PUT /settings/{oldSection}/{oldKey}": {
    schema: updateSettingSchema,
    output: [
      Ok,
      Created,
      BadRequest,
      Unauthorized,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PUT /settings/{oldSection}/{oldKey}",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["description", "isActive", "key", "section", "value"],
          inputParams: ["oldSection", "oldKey"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /settings/{section}/{key}": {
    schema: deleteSettingSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /settings/{section}/{key}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["section", "key"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /settings/public": {
    schema: getPublicSettingsSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /settings/public",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["section"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /settings/bulk": {
    schema: bulkUpdateSettingsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /settings/bulk",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["settings"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/speciality.ts
import "zod";

// packages/client/src/inputs/speciality.ts
import { z as z48 } from "zod";
var getSpecialitiesSchema = z48.object({
  query: z48.union([z48.string(), z48.string(), z48.string()]).optional(),
  page: z48.coerce.number().int().gt(0).optional().default(1),
  pageSize: z48.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z48.enum(["name", "nameEn", "description", "descriptionEn", "createdAt"]).optional().default("createdAt"),
  sortOrder: z48.enum(["asc", "desc"]).optional().default("desc")
});
var createSpecialitySchema = z48.object({
  description: z48.string(),
  descriptionEn: z48.string().optional(),
  insuranceCode: z48.string(),
  name: z48.string(),
  nameEn: z48.string().optional()
});
var getSpecialitySchema = z48.object({ id: z48.string() });
var updateSpecialitySchema = z48.object({
  description: z48.string().optional(),
  descriptionEn: z48.string().optional(),
  insuranceCode: z48.string().optional(),
  name: z48.string().optional(),
  nameEn: z48.string().optional(),
  id: z48.string()
});
var deleteSpecialitySchema = z48.object({ id: z48.string() });

// packages/client/src/api/speciality.ts
var speciality_default = {
  "GET /specialities": {
    schema: getSpecialitiesSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /specialities",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["query", "page", "pageSize", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /specialities": {
    schema: createSpecialitySchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /specialities",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "description",
            "descriptionEn",
            "insuranceCode",
            "name",
            "nameEn"
          ],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /specialities/{id}": {
    schema: getSpecialitySchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /specialities/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /specialities/{id}": {
    schema: updateSpecialitySchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /specialities/{id}",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "description",
            "descriptionEn",
            "insuranceCode",
            "name",
            "nameEn"
          ],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /specialities/{id}": {
    schema: deleteSpecialitySchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /specialities/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/users.ts
import "zod";

// packages/client/src/inputs/users.ts
import { z as z50 } from "zod";
var getCurrentUserSchema = z50.object({}).catchall(z50.unknown());
var completeUserProfileSchema = z50.object({
  birthDate: z50.string().datetime(),
  email: z50.string().email(),
  firstName: z50.string(),
  gender: z50.string(),
  lastName: z50.string(),
  nationalId: z50.string().optional().default(""),
  parentName: z50.string().optional()
});
var updateMyProfileSchema = z50.object({
  birthDate: z50.string().datetime(),
  email: z50.string().email().optional(),
  firstName: z50.string(),
  gender: z50.string(),
  lastName: z50.string(),
  nationalId: z50.string().optional().default(""),
  parentName: z50.string().optional(),
  residency: z50.string().optional()
});
var listB2CUsersSchema = z50.object({
  query: z50.union([z50.string(), z50.string(), z50.string()]).optional(),
  page: z50.coerce.number().int().gt(0).optional().default(1),
  pageSize: z50.coerce.number().int().min(1).max(100).optional().default(50),
  sortBy: z50.string().optional().default("createdAt"),
  sortOrder: z50.enum(["asc", "desc"]).optional().default("asc")
});
var getPatientByIdSchema = z50.object({ id: z50.string().uuid() });

// packages/client/src/api/users.ts
var users_default = {
  "GET /patients/me": {
    schema: getCurrentUserSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /patients/me",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /profile": {
    schema: completeUserProfileSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType,
      InternalServerError
    ],
    toRequest(input) {
      return toRequest(
        "POST /profile",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "birthDate",
            "email",
            "firstName",
            "gender",
            "lastName",
            "nationalId",
            "parentName"
          ],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /profile": {
    schema: updateMyProfileSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType,
      NotImplemented
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /profile",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "birthDate",
            "email",
            "firstName",
            "gender",
            "lastName",
            "nationalId",
            "parentName",
            "residency"
          ],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /patients/b2c": {
    schema: listB2CUsersSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /patients/b2c",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["query", "page", "pageSize", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /patients/{id}": {
    schema: getPatientByIdSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /patients/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/webhooks.ts
import "zod";

// packages/client/src/inputs/webhooks.ts
import { z as z52 } from "zod";
var beforeUserCreatedSchema = z52.object({
  id: z52.string(),
  phoneNumber: z52.string()
});

// packages/client/src/api/webhooks.ts
var webhooks_default = {
  "POST /webhooks/before-user-created": {
    schema: beforeUserCreatedSchema,
    output: [
      Created,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /webhooks/before-user-created",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["id", "phoneNumber"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/schemas.ts
var schemas_default = {
  ...meeting_default,
  ...users_default,
  ...btob_patients_default,
  ...notifications_default,
  ...doctor_default,
  ...coordinator_default,
  ...auth_default,
  ...speciality_default,
  ...medication_default,
  ...labtest_default,
  ...icd10_default,
  ...diagnosis_default,
  ...review_default,
  ...files_default,
  ...webhooks_default,
  ...payments_default,
  ...promo_code_default,
  ...reservation_default,
  ...second_opinion_default,
  ...printing_request_default,
  ...availability_default,
  ...settings_default,
  ...report_default,
  ...homecare_default,
  ...homecare_availability_default,
  ...ingest_default
};

// packages/client/src/client.ts
import z55 from "zod";

// packages/client/src/http/parser.ts
import "zod";
var ParseError = class extends Error {
  data;
  constructor(data) {
    super("Validation failed");
    this.name = "ParseError";
    this.data = data;
  }
};
function parseInput(schema, input) {
  const result = schema.safeParse(input);
  if (!result.success) {
    const error = result.error.flatten((issue) => issue);
    throw new ParseError(error);
  }
  return result.data;
}

// packages/client/src/client.ts
var optionsSchema = z55.object({
  token: z55.union([
    z55.string(),
    z55.function().returns(z55.union([z55.string(), z55.promise(z55.string())]))
  ]).optional().transform(async (token) => {
    if (!token) return void 0;
    if (typeof token === "function") {
      token = await Promise.resolve(token());
    }
    return `Bearer ${token}`;
  }).describe(
    "Bearer token for authentication. Can be a string or a function that returns a string."
  ),
  fetch: fetchType.describe(
    "Custom fetch implementation. Defaults to globalThis.fetch."
  ),
  baseUrl: z55.string().describe("Base URL of the API server."),
  headers: z55.record(z55.string()).optional().describe("Default headers to include in all requests."),
  skipValidation: z55.boolean().optional().describe(
    "Skip request input validation. Client options and TypeScript types still enforce correct usage."
  )
});
var VirtualCare = class {
  options;
  constructor(options) {
    this.options = options;
  }
  /** Sends a request and returns the unwrapped response data. Delegates to the standalone {@link request} function. */
  async request(endpoint, input, options) {
    return request(this, endpoint, input, options).then(function unwrap(it) {
      if (it instanceof APIResponse) {
        return it.data;
      }
      return it;
    });
  }
  /** Builds a ready-to-send request without sending it. Delegates to the standalone {@link prepare} function. */
  async prepare(endpoint, input, options) {
    return prepare(this, endpoint, input, options);
  }
  async defaultHeaders() {
    const options = await optionsSchema.parseAsync(this.options);
    return {
      ...{ authorization: options["token"] },
      ...options.headers
    };
  }
  async defaultInputs() {
    const options = await optionsSchema.parseAsync(this.options);
    return {};
  }
  setOptions(options) {
    this.options = {
      ...this.options,
      ...options
    };
  }
};
async function request(client, endpoint, input, requestOptions) {
  const route = schemas_default[endpoint];
  const options = await optionsSchema.parseAsync(client.options);
  const withDefaultInputs = Object.assign({}, {}, input);
  const parsedInput = options.skipValidation ? withDefaultInputs : parseInput(route.schema, withDefaultInputs);
  const result = await route.dispatch(parsedInput, {
    fetch: options.fetch,
    interceptors: [
      createHeadersInterceptor(
        { ...{ authorization: options["token"] }, ...options.headers },
        requestOptions?.headers ?? {}
      ),
      createBaseUrlInterceptor(options.baseUrl)
    ],
    signal: requestOptions?.signal
  });
  return result;
}
async function prepare(client, endpoint, input, requestOptions) {
  const route = schemas_default[endpoint];
  const options = await optionsSchema.parseAsync(client.options);
  const withDefaultInputs = Object.assign({}, {}, input);
  const parsedInput = options.skipValidation ? withDefaultInputs : parseInput(route.schema, withDefaultInputs);
  const interceptors = [
    createHeadersInterceptor(
      { ...{ authorization: options["token"] }, ...options.headers },
      requestOptions?.headers ?? {}
    ),
    createBaseUrlInterceptor(options.baseUrl)
  ];
  let config = route.toRequest(parsedInput);
  for (const interceptor of interceptors) {
    if (interceptor.before) {
      config = await interceptor.before(config);
    }
  }
  return {
    ...config,
    parse: (response) => parse2(route.output, response, (d) => d)
  };
}
export {
  APIError,
  APIResponse,
  Accepted,
  BadGateway,
  BadRequest,
  Conflict,
  Created,
  Forbidden,
  GatewayTimeout,
  Gone,
  InternalServerError,
  MethodNotAllowed,
  NoContent,
  NotAcceptable,
  NotFound,
  NotImplemented,
  Ok,
  ParseError,
  PayloadTooLarge,
  PaymentRequired,
  PreconditionFailed,
  ServiceUnavailable,
  TooManyRequests,
  Unauthorized,
  UnprocessableEntity,
  UnsupportedMediaType,
  VirtualCare,
  activateHomeCareVendorSchema,
  addVendorServiceSchema,
  assignDoctorToPrintingRequestSchema,
  assignDoctorToReservationSchema,
  assignDoctorToSecondOpinionSchema,
  attachPrintingLinkSchema,
  attachReportSchema,
  beforeUserCreatedSchema,
  bulkUpdateSettingsSchema,
  cancelReservationSchema,
  changePasswordSchema,
  checkReservationAvailabilitySchema,
  completeUserProfileSchema,
  confirmHomeCareReservationSchema,
  confirmPrintingRequestSchema,
  confirmReservationSchema,
  confirmSecondOpinionSchema,
  createB2BAppointmentSchema,
  createB2BPatientSchema,
  createB2BPrintingRequestSchema,
  createB2BSecondOpinionSchema,
  createCoordinatorSchema,
  createDiagnosisSchema,
  createDoctorSchema,
  createHomeCareReservationSchema,
  createHomeCareServiceSchema,
  createMeetingReviewSchema,
  createPaymentSchema,
  createPromoCodeSchema,
  createSettingSchema,
  createSpecialitySchema,
  deleteCoordinatorSchema,
  deleteDoctorSchema,
  deleteHomeCareVendorSchema,
  deletePromoCodeSchema,
  deleteSettingSchema,
  deleteSpecialitySchema,
  finishHomeCareReservationByPatientSchema,
  finishHomeCareReservationByVendorSchema,
  getAllReservationsSchema,
  getAllSecondOpinionsSchema,
  getAvailableDoctorsForPrintingRequestSchema,
  getAvailableDoctorsForReservationSchema,
  getAvailableDoctorsForSecondOpinionSchema,
  getAvailableSlotsBySpecialitySchema,
  getAvailableSlotsForDoctorSchema,
  getAvailableSlotsForHomeCareVendorSchema,
  getB2BPatientSchema,
  getCoordinatorSchema,
  getCurrentCoordinatorSchema,
  getCurrentDoctorSchema,
  getCurrentHomeCareClinicVendorSchema,
  getCurrentHomeCareVendorSchema,
  getCurrentUserSchema,
  getDiagnosesByDoctorSchema,
  getDiagnosesByPatientSchema,
  getDiagnosesByReservationSchema,
  getDiagnosisByIdSchema,
  getDiagnosisLabtestsPdfSchema,
  getDiagnosisPrescriptionPdfSchema,
  getDiagnosisReportPdfSchema,
  getDiagnosisSickLeavePdfSchema,
  getDoctorAvailabilitySchema,
  getDoctorEarningsSchema,
  getDoctorReservationStatsSchema,
  getDoctorSchema,
  getFileAccessSchema,
  getFileByIdSchema,
  getHomeCareReservationByIdSchema,
  getHomeCareReservationForPatientByIdSchema,
  getHomeCareReservationStatsSchema,
  getHomeCareReservationsForPatientSchema,
  getHomeCareReservationsForVendorSchema,
  getHomeCareServiceSchema,
  getHomeCareVendorAvailabilitySchema,
  getHomeCareVendorForBookingSchema,
  getIcd10CodeByIdSchema,
  getIcd10CodesSchema,
  getLabTestByIdSchema,
  getLabTestsSchema,
  getMedicationsSchema,
  getMeetingReviewSchema,
  getMeetingsSchema,
  getMonthlyAvailableSlotsForHomeCareVendorSchema,
  getMyAvailabilitySchema,
  getMyDiagnosesSchema,
  getMyHomeCareAvailabilitySchema,
  getMyHomeCareOverridesSchema,
  getMyOverridesSchema,
  getMyPatientDiagnosesSchema,
  getPatientByIdSchema,
  getPrintingRequestByIdSchema,
  getPrintingRequestsForDoctorSchema,
  getPrintingRequestsForPatientSchema,
  getPromoCodeSchema,
  getPromoCodesSchema,
  getPublicSettingsSchema,
  getRefundEligibilitySchema,
  getReservationAvailableSlotsUntilMonthEndSchema,
  getReservationByIdSchema,
  getReservationDiagnosesSchema,
  getReservationLabtestsPdfSchema,
  getReservationPrescriptionPdfSchema,
  getReservationReportPdfSchema,
  getReservationReviewsSchema,
  getReservationsForDoctorSchema,
  getReservationsForPatientSchema,
  getReviewCategoriesSchema,
  getSecondOpinionByIdSchema,
  getSecondOpinionReportPdfSchema,
  getSecondOpinionsForDoctorSchema,
  getSecondOpinionsForPatientSchema,
  getSettingsSchema,
  getSickLeaveCertificatePdfSchema,
  getSignedUrlSchema,
  getSpecialitiesSchema,
  getSpecialitySchema,
  joinMeetingAsGuestSchema,
  joinMeetingSchema,
  linkHomeCareVendorSchema,
  listAllHomeCareReservationsSchema,
  listB2BPatientsSchema,
  listB2BReservationsSchema,
  listB2BSecondOpinionsSchema,
  listB2CUsersSchema,
  listCoordinatorsSchema,
  listDoctorsSchema,
  listHomeCareServicesSchema,
  listHomeCareVendorsForPatientsSchema,
  listHomeCareVendorsSchema,
  listHomecarePaymentsSchema,
  listNotificationsSchema,
  listPrintingRequestsSchema,
  listSecondOpinionPaymentsSchema,
  listStandardPaymentsSchema,
  listVendorServicesSchema,
  markAllNotificationsAsReadSchema,
  markNotificationAsReadSchema,
  parseInput,
  patientCancelHomeCareReservationSchema,
  postEvlogingestSchema,
  prepare,
  referPrintingRequestSchema,
  referSecondOpinionToAnotherDoctorSchema,
  referToAnotherDoctorSchema,
  registerPushTokenSchema,
  rejectFollowupTimeSchema,
  rejectHomeCareVendorSchema,
  removePushTokenSchema,
  removeVendorServiceSchema,
  request,
  requestMoreInfoSchema,
  requestMoreInfoSecondOpinionSchema,
  scheduleFollowupSchema,
  setDoctorAvailabilitySchema,
  setDoctorOverridesSchema,
  setHomeCareVendorAvailabilitySchema,
  setHomeCareVendorOverridesSchema,
  submitAdditionalInfoSchema,
  submitInfoSecondOpinionSchema,
  suspendHomeCareVendorSchema,
  unsuspendHomeCareVendorSchema,
  updateB2BPatientQuotasSchema,
  updateCoordinatorSchema,
  updateDiagnosisSchema,
  updateDoctorSchema,
  updateHomeCareClinicVendorSchema,
  updateHomeCareIndividualVendorSchema,
  updateHomeCareVendorSchema,
  updateMyProfileSchema,
  updatePromoCodeSchema,
  updateSettingSchema,
  updateSpecialitySchema,
  updateVendorServicePriceSchema,
  validatePromoCodeSchema,
  verifyPaymentMobileSchema
};
//# sourceMappingURL=index.js.map
